import {GeneralViewCompetence} from 'utils/types/newReviewed';
import {Button} from 'components/ui';
import {useEffect, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import truncateStringByWords from 'utils/functions/truncateStringByWords';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {useParams} from 'react-router-dom';
import styles from './BottomMenu.module.scss';
import {AccordeonItem} from '../Accordeon';

interface BottomMenuProps {
  items: GeneralViewCompetence[];
  progressLimit: number;
}

interface ItemColors {
  backgroundColor: string;
  iconColor: string;
  borderColor?: string;
}

const unselectedColors: Record<AccordeonItem['variant'], ItemColors> = {
  positive: {
    backgroundColor: '#C6F1D3',
    iconColor: '#2FAC66',
  },
  attention: {
    backgroundColor: '#FFF5E3',
    iconColor: '#F7BB49',
  },
  negative: {
    backgroundColor: '#F8DADC',
    iconColor: '#E94B56',
  },
  empty: {
    backgroundColor: 'f2f2f2',
    iconColor: '#FFFFFF',
  },
};

const selectedColors: Record<AccordeonItem['variant'], ItemColors> = {
  positive: {
    backgroundColor: '#2FAC66',
    iconColor: '#FFFFFF',
    borderColor: '#306230',
  },
  attention: {
    backgroundColor: '#F7BB49',
    iconColor: '#FFFFFF',
    borderColor: '#cc9706',
  },
  negative: {
    backgroundColor: '#E94B56',
    iconColor: '#FFFFFF',
    borderColor: '#962F36',
  },
  empty: {
    backgroundColor: '',
    iconColor: '',
  },
};

function BottomMenu({items, progressLimit}: BottomMenuProps) {
  const [selectedItem, setSelectedItem] =
    useState<GeneralViewCompetence | null>();
  const [showFullDescriptionText, setShowFullDescriptionText] = useState(false);

  const {composition_id} = useParams();

  const {trackEvent} = useTrackEvent();

  // Always start with the first item (the smaller) as the selected one
  useEffect(() => {
    if (items[0]?.onClick) {
      handleItemClick(items[0]);
    }
  }, [items.length]);

  function handleItemClick(item: GeneralViewCompetence) {
    if (selectedItem?.name === item.name) {
      setSelectedItem(null);
      item.onClick?.(false);
    } else {
      // Only track the event when user actually click the competence, rather than always on mount (line 61)
      if (selectedItem !== null) {
        trackEvent('step3_feedback_competence_expanded', {
          composition: {
            composition_id: Number(composition_id),
          },
          competence: {
            competence_id: selectedItem?.id ?? 0,
          },
        });
      }
      setSelectedItem(item);
      item.onClick?.(true);
    }
  }

  return (
    <aside>
      {!!selectedItem && (
        <section
          className={`${styles.competenceCard} ${styles[selectedItem.variant]}`}
          data-focusid="competenceCard"
        >
          <header
            className={`${styles.header} ${styles[selectedItem.variant]}`}
          >
            <FontAwesomeIcon
              icon={selectedItem.icon ?? ['loader']}
              color={unselectedColors[selectedItem?.variant].iconColor}
              size="2x"
            />
            <div className={styles.title}>
              <h4>{selectedItem?.nickname}</h4>
              <h5>
                {selectedItem?.name} -{' '}
                <b className={styles[selectedItem.variant]}>
                  {selectedItem?.score} pontos
                </b>
              </h5>
            </div>
          </header>
          <progress value={selectedItem?.score} max={progressLimit} />

          <div className={styles.content}>
            <article
              className={`
            ${styles.feedbackCard}
            ${styles[selectedItem.variant]}
            ${showFullDescriptionText ? styles.expanded : ''}`}
            >
              <span>
                {showFullDescriptionText && (
                  <>
                    <h4>Pontos Positivos</h4>
                    <p>
                      {showFullDescriptionText
                        ? selectedItem?.positiveComment
                        : truncateStringByWords(
                            selectedItem?.positiveComment || '',
                            12,
                          )}
                    </p>
                  </>
                )}

                <h4>
                  {selectedItem?.score === 200 || selectedItem?.score === 10
                    ? 'Dicas extras'
                    : 'Como melhorar?'}
                </h4>
                <p>
                  {showFullDescriptionText
                    ? selectedItem?.attentionComment
                    : truncateStringByWords(
                        selectedItem?.attentionComment || '',
                        12,
                      )}
                </p>
              </span>

              <Button
                variant="primary"
                onClick={() =>
                  setShowFullDescriptionText(!showFullDescriptionText)
                }
                text={showFullDescriptionText ? 'Ver menos' : 'Ver mais'}
                type="button"
                size="small"
                backgroundColor="#ffffff"
                borderColor="#D9D9D9"
                leftIcon={{icon: 'eye'}}
              />
            </article>
          </div>
        </section>
      )}
      <div className={styles.bottomMenu} data-focusid="bottomMenu">
        <div className={styles.competences}>
          {items.map((item) => (
            <span key={item.name}>
              <Button
                circular
                variant="secondary"
                boxShadow={
                  selectedItem?.name === item.name
                    ? `0px 4px 16px 0px ${
                        selectedColors[item?.variant].borderColor
                      }80`
                    : ''
                }
                borderColor={
                  selectedItem?.name === item.name
                    ? selectedColors[item?.variant].borderColor
                    : ''
                }
                backgroundColor={
                  selectedItem?.name === item.name
                    ? selectedColors[item?.variant].backgroundColor
                    : unselectedColors[item?.variant].backgroundColor
                }
                leftIcon={{
                  icon: item.icon,
                  color:
                    selectedItem?.name === item.name
                      ? selectedColors[item?.variant].iconColor
                      : unselectedColors[item?.variant].iconColor,
                  spin: item.variant === 'empty',
                }}
                type="button"
                onClick={() => handleItemClick(item)}
                disabled={item.variant === 'empty'}
              />

              <p>
                {item?.variant !== 'empty'
                  ? `C${item.name?.slice(-1)}`
                  : 'C...'}
              </p>
            </span>
          ))}
        </div>
      </div>
    </aside>
  );
}

export default BottomMenu;
